import { spawn } from "child_process";
import * as path from "path";

import { ensureDir, outputFile, pathExists } from "fs-extra";
import pLimit from "p-limit";

import { ILogger } from "../../Commons/Logging/ILogger";
import { getLogger } from "../../Commons/Logging/StaticLogger";
import { ObservableValueProducerBase } from "../../Commons/TasksCore/ChainableValueProducer";
import { reject } from "../../../../Common/TypingUtils";
import { defaultRegistry } from "../../Commons/Defaults";

export class AcquirePublishedEngineTask extends ObservableValueProducerBase<string> {
    private readonly logger: ILogger;
    private readonly version: string;
    private readonly cacheDirectory: string;
    private acquired = false;
    private readonly useVersionFromCache: boolean;
    private readonly acquireLimit = pLimit(1);

    public constructor(
        version: string,
        candyModulesDirectory: string,
        useVersionFromCache: boolean,
        logger: ILogger = getLogger()
    ) {
        super();
        this.logger = logger.withPrefix("ENGINE");
        this.version = version;
        this.cacheDirectory = candyModulesDirectory;
        this.useVersionFromCache = useVersionFromCache;
    }

    public async getCurrentValue(): Promise<string> {
        const engineDirectory = path.join(this.cacheDirectory, "engines", this.version);
        const useGeneratorFromCache = this.useVersionFromCache && (await pathExists(engineDirectory));
        if (!this.acquired && !useGeneratorFromCache) {
            await this.acquireLimit(async () => {
                if (!this.acquired && !useGeneratorFromCache) {
                    await this.prepareEngineDirectory(engineDirectory);
                    this.logger.log(`Acquiring engine v${this.version}`);
                    await this.execute(engineDirectory, `npm install @kontur.candy/engine@${this.version}`);
                    this.acquired = true;
                }
            });
        }
        return path.resolve(engineDirectory, "node_modules", "@kontur.candy", "engine", "public");
    }

    protected async beginListening(): Promise<void> {
        // Нет предков и тут нечего наблюдать
    }

    protected async stopListening(): Promise<void> {
        // Нет предков и тут нечего наблюдать
    }

    private async prepareEngineDirectory(generatorDirectory: string): Promise<void> {
        await ensureDir(generatorDirectory);
        await outputFile(
            path.resolve(generatorDirectory, "package.json"),
            `{ "name": "engine", "version": "1.0.0", "description": "Engine", "author": "" }`
        );
    }

    private execute(generatorDirectory: string, command: string): Promise<void> {
        const workingDirectory = generatorDirectory;
        const [executable, ...executableArguments] = (
            (process.platform === "linux" ? "unbuffer " : "") + command
        ).split(" ");
        return new Promise<void>((resolve, rejectPromise) => {
            const { npm_config_registry: _, ...env } = process.env;
            const watchProcess = spawn(executable ?? reject(), executableArguments, {
                cwd: workingDirectory,
                shell: true,
                windowsHide: true,
                stdio: "pipe",
                env: {
                    ...env,
                    NPM_CONFIG_REGISTRY: defaultRegistry,
                },
            });
            watchProcess.stdout.on("data", (data: Buffer | string) => {
                this.logger.verbose(data.toString());
            });
            watchProcess.stderr.on("data", (data: Buffer | string) => {
                this.logger.verbose(data.toString());
            });
            watchProcess.on("exit", code => {
                if (code !== 0) {
                    rejectPromise(new Error(`Command ${command} exited with code ${code}`));
                } else {
                    resolve();
                }
            });
        });
    }
}
